// apps/provider/src/services/api/veradigm.js
// Veradigm EHR integration methods
//
// Methods: getVeradigmToken, authenticateVeradigmUser, callVeradigmAction,
//          getVeradigmServerInfo, searchVeradigmPatients, getVeradigmPatient,
//          clearVeradigmToken, initiateVeradigmAuth, completeVeradigmAuth,
//          checkVeradigmConnection, getVeradigmPatients, getVeradigmEncounters
//
// Response types: @prism-ehr/shared → VeradigmTokenResponse, VeradigmMagicResponse,
//                 VeradigmConnectionStatus, VeradigmPatient, VeradigmEncounter

import { VERADIGM_TOKEN_KEY, VERADIGM_TIMESTAMP_KEY, VERADIGM_TOKEN_TIMEOUT_MS } from './client';

/**
 * @returns {Object} Veradigm domain methods (bound to `this` of the composed apiService)
 */
export function createVeradigmMethods() {
  return {
    /**
     * Get or refresh a Veradigm authentication token.
     * Returns cached token if still valid, otherwise requests a new one.
     *
     * Xano: GET /veradigm/get-token
     * Xano file: 548_veradigm_get_token_GET.xs
     *
     * @returns {Promise<string>} The Veradigm token
     */
    async getVeradigmToken() {
      const token = localStorage.getItem(VERADIGM_TOKEN_KEY);
      const timestamp = localStorage.getItem(VERADIGM_TIMESTAMP_KEY);
      const now = Date.now();

      if (token && timestamp && now - parseInt(timestamp, 10) < VERADIGM_TOKEN_TIMEOUT_MS) {
        return token;
      }

      try {
        const response = await this.request('/veradigm/get-token', {
          method: 'GET'
        });

        if (!response.success || !response.token) {
          throw new Error(response.error || 'Failed to get Veradigm token');
        }

        localStorage.setItem(VERADIGM_TOKEN_KEY, response.token);
        localStorage.setItem(VERADIGM_TIMESTAMP_KEY, now.toString());

        return response.token;
      } catch (error) {
        console.error('[API] Error getting Veradigm token:', error);
        throw error;
      }
    },

    /**
     * Authenticate a user with Veradigm EHR credentials.
     *
     * Xano: POST /veradigm/magic
     * Xano file: 549_veradigm_magic_POST.xs
     *
     * @param {string} ehrUsername
     * @param {string} password
     * @returns {Promise<import('@prism-ehr/shared').VeradigmMagicResponse>}
     */
    async authenticateVeradigmUser(ehrUsername, password) {
      const token = await this.getVeradigmToken();

      return this.request('/veradigm/magic', {
        method: 'POST',
        body: JSON.stringify({
          action: 'GetUserAuthentication',
          patientId: '',
          token,
          ehrUser: ehrUsername,
          param1: password,
          param2: '',
          param3: '',
          param4: '',
          param5: '',
          param6: '',
          data: ''
        })
      });
    },

    /**
     * Call a generic Veradigm action via the magic endpoint.
     * Handles token refresh on auth errors.
     *
     * Xano: POST /veradigm/magic
     * Xano file: 549_veradigm_magic_POST.xs
     *
     * @param {string} action - The Veradigm action to call
     * @param {string} [patientId='']
     * @param {Object} [params={}] - Additional params (param1-param6, data)
     * @param {string} [ehrUser='jmedici']
     * @returns {Promise<import('@prism-ehr/shared').VeradigmMagicResponse>}
     */
    async callVeradigmAction(action, patientId = '', params = {}, ehrUser = 'jmedici') {
      try {
        const token = await this.getVeradigmToken();
        localStorage.setItem(VERADIGM_TIMESTAMP_KEY, Date.now().toString());

        return this.request('/veradigm/magic', {
          method: 'POST',
          body: JSON.stringify({
            action,
            patientId,
            token,
            ehrUser,
            param1: params.param1 || '',
            param2: params.param2 || '',
            param3: params.param3 || '',
            param4: params.param4 || '',
            param5: params.param5 || '',
            param6: params.param6 || '',
            data: params.data || ''
          })
        });
      } catch (error) {
        console.error(`[API] Error calling Veradigm ${action}:`, error);

        if (error.message && (
          error.message.includes('token') ||
          error.message.includes('Token') ||
          error.message.includes('authentication') ||
          error.message.includes('Authentication')
        )) {
          localStorage.removeItem(VERADIGM_TOKEN_KEY);
          localStorage.removeItem(VERADIGM_TIMESTAMP_KEY);
        }

        throw error;
      }
    },

    /**
     * Get Veradigm EHR server information.
     *
     * Xano: POST /veradigm/magic (action: GetServerInfo)
     * Xano file: 549_veradigm_magic_POST.xs
     *
     * @param {string} [ehrUser='jmedici']
     * @returns {Promise<import('@prism-ehr/shared').VeradigmMagicResponse>}
     */
    async getVeradigmServerInfo(ehrUser = 'jmedici') {
      return this.callVeradigmAction('GetServerInfo', '', {}, ehrUser);
    },

    /**
     * Search for patients in Veradigm EHR.
     *
     * Xano: POST /veradigm/magic (action: SearchPatients)
     * Xano file: 549_veradigm_magic_POST.xs
     *
     * @param {string} searchTerm
     * @param {string} [ehrUser='jmedici']
     * @returns {Promise<import('@prism-ehr/shared').VeradigmMagicResponse>}
     */
    async searchVeradigmPatients(searchTerm, ehrUser = 'jmedici') {
      return this.callVeradigmAction('SearchPatients', '', { param1: searchTerm }, ehrUser);
    },

    /**
     * Get a specific patient from Veradigm EHR.
     *
     * Xano: POST /veradigm/magic (action: GetPatient)
     * Xano file: 549_veradigm_magic_POST.xs
     *
     * @param {string} patientId
     * @param {string} [ehrUser='jmedici']
     * @returns {Promise<import('@prism-ehr/shared').VeradigmMagicResponse>}
     */
    async getVeradigmPatient(patientId, ehrUser = 'jmedici') {
      return this.callVeradigmAction('GetPatient', patientId, {}, ehrUser);
    },

    /**
     * Clear cached Veradigm token (call on logout).
     * No Xano endpoint — localStorage cleanup only.
     */
    clearVeradigmToken() {
      localStorage.removeItem(VERADIGM_TOKEN_KEY);
      localStorage.removeItem(VERADIGM_TIMESTAMP_KEY);
    },

    /**
     * Initiate Veradigm OAuth/connection flow.
     *
     * Xano: POST /veradigm/initiate-auth
     * Xano file: 506_veradigm_initiate_auth_POST.xs
     *
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').VeradigmAuthInitResponse>}
     */
    async initiateVeradigmAuth(userId) {
      try {
        const response = await this.request('/veradigm/initiate-auth', {
          method: 'POST',
          body: JSON.stringify({ user_id: userId })
        });
        return response;
      } catch (error) {
        console.error('Error initiating Veradigm auth:', error);
        throw error;
      }
    },

    /**
     * Complete Veradigm OAuth callback.
     *
     * Xano: POST /veradigm/callback
     * Xano file: 512_veradigm_callback_POST.xs
     *
     * @param {string} userId
     * @param {string} code
     * @param {string} state
     * @returns {Promise<import('@prism-ehr/shared').VeradigmAuthCallbackResponse>}
     */
    async completeVeradigmAuth(userId, code, state) {
      try {
        const response = await this.request('/veradigm/callback', {
          method: 'POST',
          body: JSON.stringify({
            user_id: userId,
            code,
            state
          })
        });
        return response;
      } catch (error) {
        console.error('Error completing Veradigm auth:', error);
        throw error;
      }
    },

    /**
     * Check Veradigm connection status for a user.
     *
     * Xano: GET /veradigm/connection-status
     * Xano file: 556_veradigm_connection_status_GET.xs
     *
     * @param {string} userId
     * @returns {Promise<import('@prism-ehr/shared').VeradigmConnectionStatus>}
     */
    async checkVeradigmConnection(userId) {
      try {
        const response = await this.request(`/veradigm/connection-status?user_id=${userId}`, {
          method: 'GET'
        });
        return response;
      } catch (error) {
        console.error('Error checking Veradigm connection:', error);
        throw error;
      }
    },

    /**
     * Get patients from Veradigm with filtering.
     *
     * Xano: GET /veradigm/patients
     * Xano file: 557_veradigm_patients_GET.xs
     *
     * @param {string} userId
     * @param {string} organizationId
     * @param {string} letter
     * @param {string} firstName
     * @returns {Promise<import('@prism-ehr/shared').VeradigmPatient[]>}
     */
    async getVeradigmPatients(userId, organizationId, letter, firstName) {
      return this.request(`/veradigm/patients?user_id=${userId}&organization_id=${organizationId}&letter=${letter}&first_name=${firstName}`, {
        method: 'GET'
      });
    },

    /**
     * Get encounters from Veradigm.
     *
     * Xano: GET /veradigm/encounters
     * Xano file: not found in staging
     *
     * @param {string} userId
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').VeradigmEncounter[]>}
     */
    async getVeradigmEncounters(userId, organizationId) {
      return this.request(`/veradigm/encounters?user_id=${userId}&organization_id=${organizationId}`, {
        method: 'GET'
      });
    },
  };
}
